import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import Protected from './protected';
import PublicRoute from './public';
import LegacyAuthRoute from './LegacyAuthRoute';
import { RouteLoader, NotFound } from '../components';
import { ForgetPassword } from '../pages/SignIn/ForgetPassword';
import ResetPassword from '../pages/SignIn/ResetPassword';
import SignIn from '../pages/SignIn/SignIn';
import LoginWithOTP from '../pages/SignIn/LoginWithOTP';
import RequestApprovalTemplate from '../pages/RequestApprovalTemplate';
import { SystemRoles } from '../enums/common';
import RoleProtectedRoute from './RoleProtectedRoute';
import Onboarding from '../pages/SignIn/Onboarding';
import SecureMaskingDashboard from '../components/common/components/SecureMaskingDashboard';
import DeviceLimitPage from '../pages/SignIn/DeviceLimitPage';

import PaymentReminderCustomerBuckets from '../pages/paymentReminder/PaymentReminderCustomerBuckets';
import ExchangeRateMapping from '../components/common/components/ExchangeRateMapping';
import TaxCodeMapping from '../components/common/components/TaxCode';
import CustomerMaterialMapping from '../components/common/components/CustomerMaterialMapping';
import CompanyGroupModuleMapping from '../components/common/components/CompanyGroupModuleMapping';
import CronJobLogs from '../components/common/components/CronJobLogs';
import ExecuteQuery from '../components/common/components/ExecuteQuery';
// Lazy load pages

const aiAssistantEnabled = import.meta.env.VITE_AI_ASSISTANT_ENABLED === 'true';

// const SignUp = lazy(() => import('../pages/SignUp/SignUp'));
const RequisitionHome = lazy(
  () => import('../pages/Requisitions/RequisitionHome'),
);
const AddRequisition = lazy(
  () => import('../pages/Requisitions/AddRequisition'),
);
const RequisitionDetailPage = lazy(
  () => import('../pages/Requisitions/RequisitionDetail'),
);
const RequisitionApproval = lazy(
  () => import('../pages/Requisitions/RequisitionApproval'),
);
const RequisitionReport = lazy(
  () => import('../pages/Requisitions/RequisitionReport'),
);
const UsersAccessControl = lazy(
  () => import('../pages/UserAccessControl/UsersAccessControl'),
);
const RolesAccess = lazy(() => import('../pages/UserAccessControl/Roles'));
const PermissionsAccess = lazy(
  () => import('../pages/UserAccessControl/Permissions'),
);
const CsvUploader = lazy(() => import('../pages/Utility/CsvUploader'));
const SendNotification = lazy(
  () => import('../pages/Utility/CustomPushNotification'),
);
const ApprovalControlPage = lazy(
  () => import('../pages/Settings/ApprovalControl'),
);
const PricingHomePage = lazy(() => import('../pages/Pricing/PricingHome'));
const PricingCreatePage = lazy(() => import('../pages/Pricing/PricingCreate'));
const PricingDetailPage = lazy(() => import('../pages/Pricing/PricingDetail'));
const PricingApprovalPage = lazy(
  () => import('../pages/Pricing/PricingApproval'),
);
const PricingReportPage = lazy(() => import('../pages/Pricing/PricingReport'));

const CustomerReports = lazy(
  () => import('../pages/CustomerStatement/CustomerReports'),
);

// // Contract Document Management
// const ContractListPage = lazy(
//   () => import('../pages/ContractManagement/contracts/ContractList'),
// );

// const ContractNewPage = lazy(
//   () => import('../pages/ContractManagement/contracts/ContractNew'),
// );
// const ContractDetailPage = lazy(
//   () => import('../pages/ContractManagement/contracts/ContractDetail'),
// );

// // Contract Template Management
// const ContractTemplateListingPage = lazy(
//   () =>
//     import(
//       '../pages/ContractManagement/contractTemplates/ContractTemplateListing'
//     ),
// );

// const ContractTemplateDetailPage = lazy(
//   () =>
//     import(
//       '../pages/ContractManagement/contractTemplates/ContractTemplateDetail'
//     ),
// );
// const ContractTemplateNewPage = lazy(
//   () =>
//     import('../pages/ContractManagement/contractTemplates/ContractTemplateNew'),
// );
// const LegacyContractDemo = lazy(
//   () => import('../pages/ContractManagement/demo/LegacyContractDemo'),
// );
const ContractDocumentTemplateList = lazy(
  () => import('../pages/ContractManagement/documentTemplates/List'),
);
const ContractDocumentTemplateCreateUpdate = lazy(
  () => import('../pages/ContractManagement/documentTemplates/CreateUpdate'),
);
const TravelReqHomePage = lazy(
  () => import('../pages/TravelRequest/TravelReqHome'),
);
const TravelReqCreatePage = lazy(
  () => import('../pages/TravelRequest/TravelReqCreate'),
);
const TravelReqDetailPage = lazy(
  () => import('../pages/TravelRequest/TravelReqDetail'),
);
const TravelReqEditPage = lazy(
  () => import('../pages/TravelRequest/TravelReqEdit'),
);
const TravelReqApprovalPage = lazy(
  () => import('../pages/TravelRequest/TravelReqApproval'),
);
const TravelReqReportPage = lazy(
  () => import('../pages/TravelRequest/TravelReqReport'),
);
const TravelHomePage = lazy(() => import('../pages/TravelRequest/TravelHome'));
const MappingsPage = lazy(() => import('../pages/TravelRequest/Mappings'));
const BusinessMappingsLandingPage = lazy(
  () => import('../pages/Settings/BusinessMappingsModulePage'),
);
const WarehouseMappingsPage = lazy(
  () => import('../pages/Warehouse/Mappings/index'),
);
const WarehouseAccessType = lazy(
  () => import('../pages/Warehouse/Mappings/AccessType'),
);
const WarehouseInventoryCategory = lazy(
  () => import('../pages/Warehouse/Mappings/InventoryCategory'),
);
const WarehouseVehicleType = lazy(
  () => import('../pages/Warehouse/Mappings/VehicleType'),
);
const WarehouseUniversalFieldSet = lazy(
  () => import('../pages/Warehouse/Mappings/UniversalFieldSet'),
);
const WarehouseTransactionType = lazy(
  () => import('../pages/Warehouse/Mappings/TransactionType'),
);
const WarehouseTransactionSubType = lazy(
  () => import('../pages/Warehouse/Mappings/TransactionSubType'),
);
const WarehouseTransactionCompanyMapping = lazy(
  () => import('../pages/Warehouse/Mappings/TransactionCompany'),
);
const WarehouseFormFieldMapping = lazy(
  () =>
    import('../pages/Warehouse/Mappings/FormFieldMapping/FormFieldMappingList'),
);
const AddWarehouseFormFieldMapping = lazy(
  () =>
    import(
      '../pages/Warehouse/Mappings/FormFieldMapping/AddWarehouseFormFieldMapping'
    ),
);
const WarehouseUserNodeMapping = lazy(
  () => import('../pages/Warehouse/Mappings/UserNodeMapping'),
);
const WarehouseNumberRange = lazy(
  () => import('../pages/Warehouse/Mappings/NumberRange'),
);
const WarehouseBatchNumberRange = lazy(
  () => import('../pages/Warehouse/Mappings/BatchNumberRange'),
);
const WarehouseSerialNumberRange = lazy(
  () => import('../pages/Warehouse/Mappings/SerialNumberRange'),
);
const WarehouseWaveRelease = lazy(
  () => import('../pages/Warehouse/Mappings/WaveRelease'),
);
const WarehouseBinCapacityMapping = lazy(
  () => import('../pages/Warehouse/Mappings/BinCapacityMapping'),
);
const WarehouseNodeType = lazy(
  () => import('../pages/Warehouse/Mappings/NodeType'),
);
const WarehouseAsset = lazy(
  () => import('../pages/Warehouse/Mappings/Asset'),
);
const WarehouseZoneGroupMapping = lazy(
  () => import('../pages/Warehouse/Mappings/ZoneGroupMapping'),
);
const WarehouseItemMapping = lazy(
  () => import('../pages/Warehouse/Mappings/ItemMapping'),
);
const BankDetailsPage = lazy(
  () => import('../pages/paymentReminder/BankDetails'),
);
const SystemConfigPage = lazy(() => import('../pages/SalesOrder/SystemConfig'));

const PerDimMappingPage = lazy(
  () => import('../components/common/components/PerDimMapping'),
);
const PerKmMappingPage = lazy(
  () => import('../components/common/components/PerKmMapping'),
);
const TravelCalendarRestrictionMapping = lazy(
  () =>
    import('../components/common/components/TravelCalendarRestrictionMapping'),
);
const ExpenseTypeAndGlAccountMappingPage = lazy(
  () =>
    import('../components/common/components/ExpenseTypeAndGlAccountMapping'),
);
const AllSalesOrders = lazy(
  () => import('../pages/SalesOrder/SalesOrderMyRequests'),
);
const SalesOrderHome = lazy(() => import('../pages/SalesOrder/SalesOrderHome'));
const SalesOrderNewDashboard = lazy(
  () => import('../pages/SalesOrder/SalesOrderNewDashboard'),
);
const SalesOrderMasterList = lazy(
  () => import('../pages/SalesOrder/SalesOrderMasterList'),
);
const SalesOrderApprovals = lazy(
  () => import('../pages/SalesOrder/SalesOrderApprovals'),
);
const SalesOrderReports = lazy(
  () => import('../pages/SalesOrder/SalesOrderReports'),
);
const SalesOrderDashboard = lazy(
  () => import('../pages/SalesOrder/SalesOrderDashboard'),
);
const SalesOrderAiReport = lazy(
  () => import('../pages/SalesOrder/SalesOrderAiReport'),
);
const SalesOrderCreateEdit = lazy(
  () => import('../pages/SalesOrder/SalesOrderCreateEdit'),
);
const SalesOrderDetails = lazy(
  () => import('../pages/SalesOrder/SalesOrderDetails'),
);
const DesignationAndModeOfJourneyMappingPage = lazy(
  () =>
    import(
      '../components/common/components/DesignationAndModeOfJourneyMapping'
    ),
);
const DesignationAndExpenseTypeMappingPage = lazy(
  () =>
    import('../components/common/components/DesignationAndExpenseTypeMapping'),
);
const ExpenseTypeLimitMappingPage = lazy(
  () => import('../components/common/components/ExpenseTypeLimitMapping'),
);
const ExpenseAntiPatternMappingPage = lazy(
  () => import('../components/common/components/ExpenseAntiPatternMapping'),
);
const DesignationAccomodationMappingPage = lazy(
  () =>
    import('../components/common/components/DesignationAccomodationMapping'),
);

const DepartmentAndCostCenterMappingPage = lazy(
  () =>
    import('../components/common/components/DepartmentAndCostCenterMApping'),
);

const DepartmentAndGLCodeMappingPage = lazy(
  () => import('../components/common/components/DepartmentAndGlCodeMapping'),
);

const ClaimAndSubClaimMappingPage = lazy(
  () => import('../components/common/components/ClaimAndSubClaimMapping'),
);
const AllContracts = lazy(
  () => import('../pages/ContractManagement/core/contracts/AllContracts'),
);
const ContractCreateEdit = lazy(
  () => import('../pages/ContractManagement/core/contracts/ContractCreateEdit'),
);
const ContractDetails = lazy(
  () => import('../pages/ContractManagement/core/contracts/ContractDetails'),
);
const ContractApprovals = lazy(
  () => import('../pages/ContractManagement/core/contracts/ContractApprovals'),
);
const ContractReports = lazy(
  () => import('../pages/ContractManagement/core/contracts/ContractReports'),
);
const ContractVendorMasterList = lazy(
  () => import('../pages/ContractManagement/masters/VendorMasterList'),
);
const PnlDashboard = lazy(
  () => import('../pages/ContractManagement/core/contracts/PnlDashboard'),
);
const ContractHomePage = lazy(
  () => import('../pages/ContractManagement/core/contracts/ContractHome'),
);
const AiAssistantHome = lazy(
  () => import('../pages/AiAssistant/AiAssistantHome'),
);

const CountryAndCityMappingPage = lazy(
  () => import('../components/common/components/CountryAndCityMapping'),
);
const HandsetMappingPage = lazy(
  () => import('../components/common/components/HandsetMapping'),
);
const ConveyanceLimitMappingPage = lazy(
  () => import('../components/common/components/ConveyanceLimitMapping'),
);

const HandsetLimitMappingPage = lazy(
  () =>
    import(
      '../components/common/components/HandsetLimitMapping/HandsetMapping'
    ),
);

const TravelFormFieldMappingCreatePage = lazy(
  () => import('../pages/TravelRequest/TravelFormFieldMappingCreate'),
);

const TravelFormFieldMappingHomePage = lazy(
  () => import('../pages/TravelRequest/TravelFormFieldMappingHome'),
);

const TravelFormFieldMappingDetailPage = lazy(
  () => import('../pages/TravelRequest/TravelFormFieldMappingDetail'),
);

const ApprovalMappingsLandingPage = lazy(
  () => import('../pages/Settings/ApprovalMappingsLanding'),
);

const TravelApprovalMappingCreatePage = lazy(
  () => import('../pages/TravelRequest/TravelApprovalMappingCreate'),
);

const TravelApprovalMappingHomePage = lazy(
  () => import('../pages/TravelRequest/TravelApprovalMappingHome'),
);

const TravelApprovalMappingDetailPage = lazy(
  () => import('../pages/TravelRequest/TravelApprovalMappingDetail'),
);

const ContractApprovalMappingHomePage = lazy(
  () =>
    import(
      '../pages/ContractManagement/core/contracts/ContractApprovalMappingHome'
    ),
);

const ContractApprovalMappingCreatePage = lazy(
  () =>
    import(
      '../pages/ContractManagement/core/contracts/ContractApprovalMappingCreate'
    ),
);

const ContractApprovalMappingDetailPage = lazy(
  () =>
    import(
      '../pages/ContractManagement/core/contracts/ContractApprovalMappingDetail'
    ),
);

const SalesOrderApprovalMappingHomePage = lazy(
  () => import('../pages/SalesOrder/SalesOrderApprovalMappingHome'),
);

const SalesOrderApprovalMappingCreatePage = lazy(
  () => import('../pages/SalesOrder/SalesOrderApprovalMappingCreate'),
);

const SalesOrderApprovalMappingDetailPage = lazy(
  () => import('../pages/SalesOrder/SalesOrderApprovalMappingDetail'),
);
// DenDashboard is commented out - using RoleBasedLanding instead
// const DenDashboard = lazy(() => import('../components/DenDashboard'));
const RoleBasedLanding = lazy(() => import('../components/RoleBasedLanding'));
const KnowledgeHubList = lazy(
  () => import('../pages/KnowledgeHub/KnowledgeHubList'),
);
const KnowledgeHubView = lazy(
  () => import('../pages/KnowledgeHub/KnowledgeHubView'),
);

const SapConfiguration = lazy(
  () => import('../pages/ContractManagement/components/SapConfiguration'),
);

const UniversalFieldSet = lazy(
  () => import('../pages/ContractManagement/components/UniversalFieldSet'),
);
const SalesOrderUniversalFieldSet = lazy(
  () => import('../pages/SalesOrder/SalesOrderUniversalFieldSet'),
);
const Mappings = lazy(
  () => import('../pages/ContractManagement/components/Mappings'),
);
const AddContractFormFieldMapping = lazy(
  () =>
    import(
      '../pages/ContractManagement/components/AddContractFormFieldMapping'
    ),
);
const ContractFormFieldMapping = lazy(
  () =>
    import('../pages/ContractManagement/components/ContractFormFieldMapping'),
);
const AddSalesOrderFormFieldMapping = lazy(
  () => import('../pages/SalesOrder/AddSalesOrderFormFieldMapping'),
);
const SalesOrderFormFieldMapping = lazy(
  () => import('../pages/SalesOrder/SalesOrderFormFieldMapping'),
);

const EmailConfiguration = lazy(
  () => import('../pages/ContractManagement/components/EmailConfiguration'),
);
const WhatsAppConfiguration = lazy(
  () => import('../pages/ContractManagement/components/WhatsAppConfiguration'),
);

const ActivityMasterTabsPage = lazy(
  () =>
    import(
      '../pages/ActivityTracking/components/Masters/ActivityMasterTabsPage'
    ),
);
const ActivityUserAssignment = lazy(
  () =>
    import(
      '../pages/ActivityTracking/components/UserAssignment/ActivityUserAssignment'
    ),
);
const MyActivity = lazy(
  () => import('../pages/ActivityTracking/components/MyActivity/MyActivity'),
);
const ActivityApprovalMappingHome = lazy(
  () =>
    import(
      '../pages/ActivityTracking/components/ApprovalMappings/ActivityApprovalMappingHome'
    ),
);
const ActivityApprovalMappingCreate = lazy(
  () =>
    import(
      '../pages/ActivityTracking/components/ApprovalMappings/ActivityApprovalMappingCreate'
    ),
);
const ActivityApprovalMappingDetail = lazy(
  () =>
    import(
      '../pages/ActivityTracking/components/ApprovalMappings/ActivityApprovalMappingDetail'
    ),
);
const ActivityApprovals = lazy(
  () =>
    import('../pages/ActivityTracking/components/Approvals/ActivityApprovals'),
);
const ActivityApprovalDetails = lazy(
  () =>
    import('../pages/ActivityTracking/components/Approvals/ApprovalDetails'),
);

const ActivityUserAssginmentDetail = lazy(
  () =>
    import(
      '../pages/ActivityTracking/components/UserAssignment/ActivityUserAssginmentDetail'
    ),
);
const MyActivityDetail = lazy(
  () =>
    import('../pages/ActivityTracking/components/MyActivity/MyActivityDetail'),
);
const ActivityMapping = lazy(
  () =>
    import(
      '../pages/ActivityTracking/components/ActivityMapping/ActivityMapping'
    ),
);

const ActivityMappingsPage = lazy(
  () => import('../pages/ActivityTracking/components/ActivityMapping'),
);

const CreateActivityMapping = lazy(
  () =>
    import(
      '../pages/ActivityTracking/components/ActivityMapping/CreateActivityMapping'
    ),
);

const ActivityCreateUserAssignment = lazy(
  () =>
    import(
      '../pages/ActivityTracking/components/UserAssignment/ActivityCreateUserAssignment'
    ),
);
const ActivityDashboard = lazy(
  () =>
    import('../pages/ActivityTracking/components/Dashboard/ActivityDashboard'),
);
const ActivityReport = lazy(
  () =>
    import(
      '../pages/ActivityTracking/components/ActivityReport/ActivityReport'
    ),
);

const ActivityGlobalFilter = lazy(
  () => import('../pages/ActivityTracking/components/EntitySelectionPage'),
);
const ActivityOwnerConfiguration = lazy(
  () =>
    import(
      '../pages/ActivityTracking/components/Masters/ActivityOwnerConfiguration'
    ),
);

const PaymentReminderHome = lazy(
  () => import('../pages/paymentReminder/PaymentReminderHome'),
);

const PaymentReminderCustomerBucketsPage = lazy(
  () => import('../pages/paymentReminder/PaymentReminderCustomerBuckets'),
);

const CategoryConfiguration = lazy(
  () => import('../pages/AdvancedPayment/master/CategoryConfiguration'),
);
const AdvancedPaymentUniversalSet = lazy(
  () => import('../pages/AdvancedPayment/master/UniversalFieldSet'),
);
const AdvancedPaymentFormFieldMapping = lazy(
  () =>
    import('../pages/AdvancedPayment/FormFieldMapping/FormFieldMappingList'),
);
const AddAdvancedPaymentFormFieldMapping = lazy(
  () =>
    import(
      '../pages/AdvancedPayment/FormFieldMapping/AddAdvancedPaymentFormFieldMapping'
    ),
);
const AdvancedPaymentApprovalMappingHome = lazy(
  () =>
    import(
      '../pages/AdvancedPayment/approval-mappings/AdvancedPaymentApprovalMappingHome'
    ),
);
const AdvancedPaymentApprovalMappingCreateEdit = lazy(
  () =>
    import(
      '../pages/AdvancedPayment/approval-mappings/AdvancedPaymentApprovalMappingCreateEdit'
    ),
);
const AdvancedPaymentHome = lazy(
  () => import('../pages/AdvancedPayment/AdvancedPaymentHome'),
);
const AdvancedPaymentCreateEdit = lazy(
  () => import('../pages/AdvancedPayment/AdvancedPaymentCreateEdit'),
);
const AdvancedPaymentDetails = lazy(
  () => import('../pages/AdvancedPayment/AdvancedPaymentDetails'),
);
const AdvancedPaymentApprovals = lazy(
  () => import('../pages/AdvancedPayment/AdvancedPaymentApprovals'),
);
const AdvancedPaymentReports = lazy(
  () => import('../pages/AdvancedPayment/AdvancedPaymentReports'),
);
const PurchaseOrderDashboard = lazy(
  () => import('../pages/PurchaseOrder/PurchaseOrderDashboard'),
);
const PurchaseOrderHome = lazy(
  () => import('../pages/PurchaseOrder/PurchaseOrderHome'),
);
const PurchaseOrderConfigurationHome = lazy(
  () => import('../pages/PurchaseOrder/PurchaseOrderConfigurationHome'),
);
const PurchaseOrderConfigurationEditor = lazy(
  () => import('../pages/PurchaseOrder/PurchaseOrderConfigurationEditor'),
);
const PurchaseOrderMasterDefaults = lazy(
  () => import('../pages/PurchaseOrder/PurchaseOrderMasterDefaults'),
);
const PurchaseOrderSettingsHub = lazy(
  () => import('../pages/PurchaseOrder/PurchaseOrderSettingsHub'),
);
const PurchaseOrderFinancialPolicies = lazy(
  () => import('../pages/PurchaseOrder/PurchaseOrderFinancialPolicies'),
);
const PurchaseOrderDetails = lazy(
  () => import('../pages/PurchaseOrder/PurchaseOrderDetails'),
);
const PurchaseOrderCreateEdit = lazy(
  () => import('../pages/PurchaseOrder/PurchaseOrderCreateEdit'),
);
const WarehouseStructure = lazy(
  () => import('../pages/Warehouse/WarehouseStructure'),
);
const WarehouseOperation = lazy(
  () => import('../pages/Warehouse/WarehouseOperation'),
);
const WarehouseOperationList = lazy(
  () => import('../pages/Warehouse/WarehouseOperationList'),
);
const WarehouseOperationDetail = lazy(
  () => import('../pages/Warehouse/WarehouseOperationDetail'),
);
const WarehouseBinDetails = lazy(
  () => import('../pages/Warehouse/WarehouseBinDetails'),
);
const WarehouseInventoryReport = lazy(
  () => import('../pages/Warehouse/WarehouseInventoryReport'),
);
const ContractDocumentEditor = lazy(
  () =>
    import(
      '../pages/ContractManagement/core/contracts/components/ContractDocumentEditor'
    ),
);
const AnalyticsDatasetList = lazy(
  () => import('../pages/AnalyticalTool/datasets/DatasetList'),
);
const AnalyticsDatasetWizard = lazy(
  () => import('../pages/AnalyticalTool/datasets/DatasetWizard'),
);
const AnalyticsDatasetDetail = lazy(
  () => import('../pages/AnalyticalTool/datasets/DatasetDetail'),
);
const AnalyticsChartList = lazy(
  () => import('../pages/AnalyticalTool/charts/ChartList'),
);
const AnalyticsChartBuilder = lazy(
  () => import('../pages/AnalyticalTool/charts/ChartBuilder'),
);
const AnalyticsDashboardList = lazy(
  () => import('../pages/AnalyticalTool/dashboards/DashboardList'),
);
const AnalyticsDashboardBuilder = lazy(
  () => import('../pages/AnalyticalTool/dashboards/DashboardBuilder'),
);
const AnalyticsDashboardViewer = lazy(
  () => import('../pages/AnalyticalTool/dashboards/DashboardViewer'),
);
const AnalyticsEmbeddedDashboard = lazy(
  () => import('../pages/AnalyticalTool/embed/EmbeddedDashboard'),
);
// Router setup with lazy-loaded components
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" errorElement={<NotFound />}>
      <Route element={<LegacyAuthRoute />}>
        <Route path="/forgot-password" element={<ForgetPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Route>
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/mail-approval" element={<RequestApprovalTemplate />} />
      <Route path="/device-limit" element={<DeviceLimitPage />} />
      <Route
        path="/analytics/embed/dashboards/:id"
        element={
          <Suspense fallback={<RouteLoader />}>
            <AnalyticsEmbeddedDashboard />
          </Suspense>
        }
      />

      <Route element={<PublicRoute />}>
        <Route
          path="signin"
          element={
            <Suspense fallback={<RouteLoader />}>
              <SignIn />
            </Suspense>
          }
        />
        <Route element={<LegacyAuthRoute />}>
          <Route
            path="login-with-otp"
            element={
              <Suspense fallback={<RouteLoader />}>
                <LoginWithOTP />
              </Suspense>
            }
          />
        </Route>
        {/* <Route
          path="signup"
          element={
            <Suspense fallback={<RouteLoader />}>
              <SignUp />
            </Suspense>
          }
        /> */}
      </Route>

      <Route element={<Protected />}>
        <Route
          index
          element={
            <Suspense fallback={<RouteLoader />}>
              <RoleBasedLanding />
            </Suspense>
          }
        />

        <Route path="knowledge-hub" errorElement={<NotFound />}>
          <Route
            index
            element={
              <Suspense fallback={<RouteLoader />}>
                <KnowledgeHubList />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <KnowledgeHubView />
              </Suspense>
            }
          />
        </Route>

        <Route path="requisition" errorElement={<NotFound />}>
          <Route index element={<NotFound />} />
          <Route
            path="orders"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequisitionHome />
              </Suspense>
            }
          />
          <Route
            path="orders/add"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AddRequisition />
              </Suspense>
            }
          />
          <Route
            path="orders/:id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequisitionDetailPage />
              </Suspense>
            }
          />
          <Route
            path="approvals"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequisitionApproval />
              </Suspense>
            }
          />
          <Route
            path="reports"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequisitionReport />
              </Suspense>
            }
          />
        </Route>

        <Route path="pricing" errorElement={<NotFound />}>
          <Route index element={<NotFound />} />
          <Route
            path="all"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PricingHomePage />
              </Suspense>
            }
          />
          <Route
            path="add"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PricingCreatePage />
              </Suspense>
            }
          />

          <Route
            path=":id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PricingDetailPage />
              </Suspense>
            }
          />

          <Route
            path="approvals"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PricingApprovalPage />
              </Suspense>
            }
          />

          <Route
            path="reports"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PricingReportPage />
              </Suspense>
            }
          />
        </Route>

        <Route path="sales-order" errorElement={<NotFound />}>
          <Route
            path="home"
            element={
              <Suspense fallback={<RouteLoader />}>
                <SalesOrderHome />
              </Suspense>
            }
          />
          <Route
            path="new-dashboard"
            element={
              <Suspense fallback={<RouteLoader />}>
                <SalesOrderNewDashboard />
              </Suspense>
            }
          />
          <Route
            path="all"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AllSalesOrders />
              </Suspense>
            }
          />
          <Route
            path="master-list"
            element={
              <Suspense fallback={<RouteLoader />}>
                <SalesOrderMasterList />
              </Suspense>
            }
          />
          <Route
            path="approvals"
            element={
              <Suspense fallback={<RouteLoader />}>
                <SalesOrderApprovals />
              </Suspense>
            }
          />
          <Route
            path="reports"
            element={
              <Suspense fallback={<RouteLoader />}>
                <SalesOrderReports />
              </Suspense>
            }
          />
          <Route
            path="dashboard"
            element={
              <Suspense fallback={<RouteLoader />}>
                <SalesOrderDashboard />
              </Suspense>
            }
          />
          <Route
            path="ai-report"
            element={
              <RoleProtectedRoute
                allowedRoles={[SystemRoles.ADMIN, SystemRoles.SALES_ORDER_FINANCE]}
              />
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<RouteLoader />}>
                  <SalesOrderAiReport />
                </Suspense>
              }
            />
          </Route>
          <Route
            path=":id/edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <SalesOrderCreateEdit />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <SalesOrderDetails />
              </Suspense>
            }
          />
        </Route>

        <Route
          path="uac"
          errorElement={<NotFound />}
          element={
            <RoleProtectedRoute
              allowedRoles={[SystemRoles.ADMIN, SystemRoles.HR_ADMIN]}
            />
          }
        >
          <Route
            path="users"
            element={
              <Suspense fallback={<RouteLoader />}>
                <UsersAccessControl />
              </Suspense>
            }
          />
          <Route
            path="roles"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RolesAccess />
              </Suspense>
            }
          />
          <Route
            path="permissions"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PermissionsAccess />
              </Suspense>
            }
          />
        </Route>

        {/* <Route path="contract" errorElement={<NotFound />}>
          <Route
            path="list"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractListPage />
              </Suspense>
            }
          />

          <Route
            path="new"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractNewPage />
              </Suspense>
            }
          />

          <Route
            path=":contractId"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractDetailPage />
              </Suspense>
            }
          />

          <Route
            path="template"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractTemplateListingPage />
              </Suspense>
            }
          />

          <Route
            path="template/new"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractTemplateNewPage />
              </Suspense>
            }
          />

          <Route
            path="template/:templateId"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractTemplateDetailPage />
              </Suspense>
            }
          />
        </Route> */}

        <Route path="utilities" errorElement={<NotFound />}>
          <Route
            path="csv-upload"
            element={
              <Suspense fallback={<RouteLoader />}>
                <CsvUploader />
              </Suspense>
            }
          />
        </Route>

        <Route path="utility" errorElement={<NotFound />}>
          <Route
            path="uploader"
            element={
              <Suspense fallback={<RouteLoader />}>
                <CsvUploader
                  pageTitle="Master Data Uploader"
                  useMasterTableNames
                />
              </Suspense>
            }
          />
          <Route
            path="execute-query"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ExecuteQuery />
              </Suspense>
            }
          />
          <Route
            path="notification"
            element={
              <RoleProtectedRoute
                allowedRoles={[
                  SystemRoles.ADMIN,
                  SystemRoles.SALES_ORDER_ADMIN,
                ]}
              />
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<RouteLoader />}>
                  <SendNotification />
                </Suspense>
              }
            />
          </Route>
        </Route>

        <Route
          path="settings"
          errorElement={<NotFound />}
          element={
            <RoleProtectedRoute
              allowedRoles={[SystemRoles.ADMIN, SystemRoles.HR_ADMIN]}
            />
          }
        >
          <Route
            path="mappings/activity-masters"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityMasterTabsPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/project"
            errorElement={<NotFound />}
            element={
              <Navigate
                to="/settings/mappings/activity-masters?tab=project"
                replace
              />
            }
          />
          <Route
            path="mappings/process"
            errorElement={<NotFound />}
            element={
              <Navigate
                to="/settings/mappings/activity-masters?tab=process"
                replace
              />
            }
          />
          <Route
            path="mappings/sub-process"
            errorElement={<NotFound />}
            element={
              <Navigate
                to="/settings/mappings/activity-masters?tab=sub-process"
                replace
              />
            }
          />
          <Route
            path="mappings/activity"
            errorElement={<NotFound />}
            element={
              <Navigate
                to="/settings/mappings/activity-masters?tab=activity"
                replace
              />
            }
          />
          <Route
            path="mappings/activity-owner"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityOwnerConfiguration />
              </Suspense>
            }
          ></Route>
          <Route
            path="mappings/workflow-hierarchy"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityDashboard />
              </Suspense>
            }
          ></Route>
          <Route
            path="mappings/activity-mapping"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityMappingsPage />
              </Suspense>
            }
          ></Route>
          <Route
            path="mappings/activity-mapping/all"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityMapping />
              </Suspense>
            }
          ></Route>
          <Route
            path="mappings/create-activity-mapping"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <CreateActivityMapping />
              </Suspense>
            }
          ></Route>

          <Route
            path="mappings/update-activity-mapping/:id"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <CreateActivityMapping />
              </Suspense>
            }
          ></Route>
          <Route
            path="mappings/exchange-rate"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ExchangeRateMapping />
              </Suspense>
            }
          />
          <Route
            path="mappings/tax-code"
            element={
              <Suspense fallback={<RouteLoader />}>
                <TaxCodeMapping />
              </Suspense>
            }
          />
          <Route
            path="mappings/customer-material-mapping"
            element={
              <Suspense fallback={<RouteLoader />}>
                <CustomerMaterialMapping />
              </Suspense>
            }
          />
          <Route
            path="mappings/company-group-module"
            element={
              <Suspense fallback={<RouteLoader />}>
                <CompanyGroupModuleMapping />
              </Suspense>
            }
          />
          <Route
            path="mappings/cron-job"
            element={
              <Suspense fallback={<RouteLoader />}>
                <CronJobLogs />
              </Suspense>
            }
          />

          <Route
            path="approvals"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ApprovalControlPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/secure-masking"
            element={
              <Suspense fallback={<RouteLoader />}>
                <SecureMaskingDashboard />
              </Suspense>
            }
          />

          <Route
            path="mappings"
            element={
              <Suspense fallback={<RouteLoader />}>
                <MappingsPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/module/common"
            element={
              <Suspense fallback={<RouteLoader />}>
                <BusinessMappingsLandingPage
                  moduleName="Common"
                  items={[
                    { title: 'Company Group Module Mapping', path: '/settings/mappings/company-group-module' },
                    { title: 'Cron Job', path: '/settings/mappings/cron-job' },
                    { title: 'System Config', path: '/settings/mappings/system-config' },
                    { title: 'Secure Masking', path: '/settings/mappings/secure-masking' },
                    { title: 'Email Configuration', path: '/settings/mappings/email-configuration' },
                    { title: 'WhatsApp Configuration', path: '/settings/mappings/whatsapp-configuration' },
                    { title: 'Bank Details', path: '/settings/mappings/bank-details' },
                    { title: 'SAP Configuration', path: '/settings/mappings/sap-configuration' },
                  ]}
                />
              </Suspense>
            }
          />
          <Route
            path="mappings/module/travel"
            element={
              <Suspense fallback={<RouteLoader />}>
                <BusinessMappingsLandingPage
                  moduleName="Travel"
                  items={[
                    { title: 'Travel Calendar Restriction', path: '/settings/mappings/travel-calendar-restriction' },
                    { title: 'Per Km Mapping', path: '/settings/mappings/per-km' },
                    { title: 'Expense Limit', path: '/settings/mappings/expense-type-limit' },
                    { title: 'Expense Anti Patterns', path: '/settings/mappings/expense-anti-patterns' },
                    { title: 'Expense & GL Code', path: '/settings/mappings/department-cost-center' },
                    { title: 'Claim Category & Subcategory', path: '/settings/mappings/claim-category-sub-claim' },
                    { title: 'Country & City', path: '/settings/mappings/country-city' },
                    { title: 'Handset Mapping', path: '/settings/mappings/user-handset' },
                    { title: 'Conveyance Limit', path: '/settings/mappings/conveyance-limit' },
                  ]}
                />
              </Suspense>
            }
          />
          <Route
            path="mappings/module/contract"
            element={
              <Suspense fallback={<RouteLoader />}>
                <BusinessMappingsLandingPage
                  moduleName="Contract"
                  items={[{ title: 'Contract form field set', path: '/settings/mappings/contract-field-set' }]}
                />
              </Suspense>
            }
          />
          <Route
            path="mappings/module/sales-order"
            element={
              <Suspense fallback={<RouteLoader />}>
                <BusinessMappingsLandingPage
                  moduleName="Sales Order"
                  items={[
                    { title: 'Sales Order form field set', path: '/settings/mappings/sales-order-field-set' },
                    { title: 'Tax Code', path: '/settings/mappings/tax-code' },
                    { title: 'Exchange Rate', path: '/settings/mappings/exchange-rate' },
                    { title: 'Customer Material Mapping', path: '/settings/mappings/customer-material-mapping' },
                    { title: 'Vehicle Type', path: '/settings/mappings/warehouse/vehicle-type' },
                  ]}
                />
              </Suspense>
            }
          />
          <Route
            path="mappings/module/activity"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityMappingsPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/module/advance-payment"
            element={
              <Suspense fallback={<RouteLoader />}>
                <BusinessMappingsLandingPage
                  moduleName="Advance Payment"
                  items={[
                    { title: 'Advanced Category', path: '/settings/mappings/advanced-category' },
                    { title: 'Advanced Payment form field set', path: '/settings/mappings/advanced-payment-field-set' },
                  ]}
                />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse"
            element={
              <Suspense fallback={<RouteLoader />}>
                <BusinessMappingsLandingPage
                  moduleName="Warehouse"
                  items={[
                    { title: 'Access Type', path: '/settings/mappings/warehouse/access-type' },
                    { title: 'Inventory Category', path: '/settings/mappings/warehouse/inventory-category' },
                    { title: 'Transaction Type', path: '/settings/mappings/warehouse/transaction-type' },
                    { title: 'Transaction Category', path: '/settings/mappings/warehouse/transaction-sub-type' },
                    { title: 'Universal Field Set', path: '/settings/mappings/warehouse/universal-field-set' },
                    { title: 'User Node Mapping', path: '/settings/mappings/warehouse/user-node-mapping' },
                    { title: 'Number Range', path: '/settings/mappings/warehouse/number-range' },
                    { title: 'Batch Number Range', path: '/settings/mappings/warehouse/batch-number-range' },
                    { title: 'Serial Number Range', path: '/settings/mappings/warehouse/serial-number-range' },
                    { title: 'Wave Release', path: '/settings/mappings/warehouse/wave-release' },
                    { title: 'Bin Capacity', path: '/settings/mappings/warehouse/bin-capacity' },
                    { title: 'Node Type', path: '/settings/mappings/warehouse/node-type' },
                    { title: 'Zone Group Mapping', path: '/settings/mappings/warehouse/zone-group-mapping' },
                  ]}
                />
              </Suspense>
            }
          />
          <Route
            path="mappings/purchase-order"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderSettingsHub />
              </Suspense>
            }
          />
          <Route
            path="mappings/bank-details"
            element={
              <Suspense fallback={<RouteLoader />}>
                <BankDetailsPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/system-config"
            element={
              <Suspense fallback={<RouteLoader />}>
                <SystemConfigPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/travel-calendar-restriction"
            element={
              <Suspense fallback={<RouteLoader />}>
                <TravelCalendarRestrictionMapping />
              </Suspense>
            }
          />
          <Route
            path="mappings/per-diem"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PerDimMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/per-km"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PerKmMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/expense-type-gl"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ExpenseTypeAndGlAccountMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/designation-mode-journey"
            element={
              <Suspense fallback={<RouteLoader />}>
                <DesignationAndModeOfJourneyMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/designation-expense-type"
            element={
              <Suspense fallback={<RouteLoader />}>
                <DesignationAndExpenseTypeMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/expense-type-limit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ExpenseTypeLimitMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/expense-anti-patterns"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ExpenseAntiPatternMappingPage />
              </Suspense>
            }
          />

          <Route
            path="mappings/designation-accomodation"
            element={
              <Suspense fallback={<RouteLoader />}>
                <DesignationAccomodationMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/department-cost-center"
            element={
              <Suspense fallback={<RouteLoader />}>
                <DepartmentAndCostCenterMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/department-gl-code"
            element={
              <Suspense fallback={<RouteLoader />}>
                <DepartmentAndGLCodeMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/claim-category-sub-claim"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ClaimAndSubClaimMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/country-city"
            element={
              <Suspense fallback={<RouteLoader />}>
                <CountryAndCityMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/user-handset"
            element={
              <Suspense fallback={<RouteLoader />}>
                <HandsetMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/conveyance-limit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ConveyanceLimitMappingPage />
              </Suspense>
            }
          />

          <Route
            path="mappings/handset-limit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <HandsetLimitMappingPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/sap-configuration"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <SapConfiguration />
              </Suspense>
            }
          ></Route>
          <Route
            path="mappings/email-configuration"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <EmailConfiguration />
              </Suspense>
            }
          ></Route>
          <Route
            path="mappings/whatsapp-configuration"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <WhatsAppConfiguration />
              </Suspense>
            }
          />
          <Route
            path="mappings/contract-field-set"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <UniversalFieldSet />
              </Suspense>
            }
          ></Route>
          <Route
            path="mappings/sales-order-field-set"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <SalesOrderUniversalFieldSet />
              </Suspense>
            }
          ></Route>
          <Route
            path="mappings/advanced-payment-field-set"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <AdvancedPaymentUniversalSet />
              </Suspense>
            }
          ></Route>
          <Route
            path="/settings/form-field-mappings"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <Mappings />
              </Suspense>
            }
          ></Route>
          <Route
            path="mappings/advanced-category"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <CategoryConfiguration />
              </Suspense>
            }
          ></Route>
          <Route
            path="mappings/warehouse"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseMappingsPage />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/access-type"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseAccessType />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/inventory-category"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseInventoryCategory />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/vehicle-type"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseVehicleType />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/universal-field-set"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseUniversalFieldSet />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/transaction-type"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseTransactionType />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/transaction-sub-type"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseTransactionSubType />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/transaction-company-mapping"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseTransactionCompanyMapping />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/user-node-mapping"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseUserNodeMapping />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/number-range"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseNumberRange />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/batch-number-range"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseBatchNumberRange />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/serial-number-range"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseSerialNumberRange />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/wave-release"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseWaveRelease />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/bin-capacity"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseBinCapacityMapping />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/node-type"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseNodeType />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/asset"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseAsset />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/activity"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseAsset />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/zone-group-mapping"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseZoneGroupMapping />
              </Suspense>
            }
          />
          <Route
            path="mappings/warehouse/item-mapping"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseItemMapping />
              </Suspense>
            }
          />
          <Route
            path="warehouse-form-field-mappings"
            errorElement={<NotFound />}
          >
            <Route
              path="add"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AddWarehouseFormFieldMapping />
                </Suspense>
              }
            ></Route>
            <Route
              path="all"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <WarehouseFormFieldMapping />
                </Suspense>
              }
            ></Route>

            <Route
              path=":id"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AddWarehouseFormFieldMapping />
                </Suspense>
              }
            ></Route>
          </Route>
          <Route
            path="contract-form-field-mappings"
            errorElement={<NotFound />}
          >
            <Route
              path="add"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AddContractFormFieldMapping />
                </Suspense>
              }
            ></Route>
            <Route
              path="all"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <ContractFormFieldMapping />
                </Suspense>
              }
            ></Route>

            <Route
              path=":id"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AddContractFormFieldMapping />
                </Suspense>
              }
            ></Route>
          </Route>
          <Route
            path="sales-order-form-field-mappings"
            errorElement={<NotFound />}
          >
            <Route
              path="add"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AddSalesOrderFormFieldMapping />
                </Suspense>
              }
            ></Route>
            <Route
              path="all"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <SalesOrderFormFieldMapping />
                </Suspense>
              }
            ></Route>
            <Route
              path=":id"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AddSalesOrderFormFieldMapping />
                </Suspense>
              }
            ></Route>
          </Route>
          {/* // travel form field mappings */}
          <Route path="form-field-mappings" errorElement={<NotFound />}>
            <Route
              path="all"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <TravelFormFieldMappingHomePage />
                </Suspense>
              }
            ></Route>
            <Route
              path="add"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <TravelFormFieldMappingCreatePage />
                </Suspense>
              }
            ></Route>
            <Route
              path=":id"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <TravelFormFieldMappingDetailPage />
                </Suspense>
              }
            ></Route>
          </Route>
          <Route
            path="advanced-payment-form-field-mappings"
            errorElement={<NotFound />}
          >
            <Route
              path="add"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AddAdvancedPaymentFormFieldMapping />
                </Suspense>
              }
            ></Route>
            <Route
              path="all"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AdvancedPaymentFormFieldMapping />
                </Suspense>
              }
            ></Route>
            <Route
              path=":id"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AddAdvancedPaymentFormFieldMapping />
                </Suspense>
              }
            ></Route>
          </Route>
          {/* // approval mappings landing page */}
          <Route
            path="approval-mappings"
            errorElement={<NotFound />}
            element={
              <Suspense fallback={<RouteLoader />}>
                <ApprovalMappingsLandingPage />
              </Suspense>
            }
          />
          {/* // travel approval mappings */}
          <Route path="approval-mappings/travel" errorElement={<NotFound />}>
            <Route
              path="all"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <TravelApprovalMappingHomePage />
                </Suspense>
              }
            ></Route>
            <Route
              path="add"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <TravelApprovalMappingCreatePage />
                </Suspense>
              }
            ></Route>
            <Route
              path=":id"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <TravelApprovalMappingDetailPage />
                </Suspense>
              }
            ></Route>
          </Route>
          {/* // contract approval mappings */}
          <Route path="approval-mappings/contract" errorElement={<NotFound />}>
            <Route
              path="all"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <ContractApprovalMappingHomePage />
                </Suspense>
              }
            ></Route>
            <Route
              path="add"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <ContractApprovalMappingCreatePage />
                </Suspense>
              }
            ></Route>
            <Route
              path=":id"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <ContractApprovalMappingDetailPage />
                </Suspense>
              }
            ></Route>
          </Route>
          {/* // sales order approval mappings */}
          <Route
            path="approval-mappings/sales-order"
            errorElement={<NotFound />}
          >
            <Route
              path="all"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <SalesOrderApprovalMappingHomePage />
                </Suspense>
              }
            ></Route>
            <Route
              path="add"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <SalesOrderApprovalMappingCreatePage />
                </Suspense>
              }
            ></Route>
            <Route
              path=":id"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <SalesOrderApprovalMappingDetailPage />
                </Suspense>
              }
            ></Route>
          </Route>

          <Route path="approval-mappings/activity" errorElement={<NotFound />}>
            <Route
              path="all"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <ActivityApprovalMappingHome />
                </Suspense>
              }
            ></Route>
            <Route
              path="add"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <ActivityApprovalMappingCreate />
                </Suspense>
              }
            ></Route>
            <Route
              path=":id"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <ActivityApprovalMappingDetail />
                </Suspense>
              }
            ></Route>
          </Route>
          {/* // advanced-payment approval mappings */}
          <Route
            path="approval-mappings/advanced-payment"
            errorElement={<NotFound />}
          >
            <Route
              path="all"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AdvancedPaymentApprovalMappingHome />
                </Suspense>
              }
            ></Route>
            <Route
              path="add"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AdvancedPaymentApprovalMappingCreateEdit />
                </Suspense>
              }
            ></Route>
            <Route
              path=":id"
              errorElement={<NotFound />}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AdvancedPaymentApprovalMappingCreateEdit />
                </Suspense>
              }
            ></Route>
          </Route>
        </Route>

        {/* ================= PAYMENT REMINDER ================= */}
        <Route
          path="payment-reminder"
          errorElement={<NotFound />}
          element={
            <RoleProtectedRoute
              allowedRoles={[
                SystemRoles.ADMIN,
                SystemRoles.EMPLOYEE,
                SystemRoles.PAYMENT_EMPLOYEE,
              ]}
            />
          }
        >
          <Route
            path="all"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PaymentReminderHome />
              </Suspense>
            }
          />
          {/* Customer Bucket / Details */}
          <Route
            path=":fileId"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PaymentReminderCustomerBuckets />
              </Suspense>
            }
          />
        </Route>

        {aiAssistantEnabled && (
          <Route
            path="ai-assistant"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AiAssistantHome />
              </Suspense>
            }
          />
        )}

        <Route path="contracts" errorElement={<NotFound />}>
          <Route
            path="home"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractHomePage />
              </Suspense>
            }
          />
          <Route
            path="all"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AllContracts />
              </Suspense>
            }
          />
          <Route
            path=":id/edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractCreateEdit />
              </Suspense>
            }
          />
          <Route
            path=":id/document-edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractDocumentEditor />
              </Suspense>
            }
          />
          <Route
            path="masters"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractVendorMasterList />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractDetails />
              </Suspense>
            }
          />
          <Route
            path="approvals"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractApprovals />
              </Suspense>
            }
          />
          <Route
            path="reports"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractReports />
              </Suspense>
            }
          />
          <Route
            path="pnl-dashboard"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PnlDashboard />
              </Suspense>
            }
          />
        </Route>
        <Route path="contracts" errorElement={<NotFound />}>
          <Route
            path="home"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractHomePage />
              </Suspense>
            }
          />
          <Route
            path="all"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AllContracts />
              </Suspense>
            }
          />
          <Route
            path="document-templates"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractDocumentTemplateList />
              </Suspense>
            }
          />
          <Route
            path="document-templates/new"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractDocumentTemplateCreateUpdate />
              </Suspense>
            }
          />
          <Route
            path="document-templates/:templateId/edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractDocumentTemplateCreateUpdate />
              </Suspense>
            }
          />
          {/* <Route
            path="demo-contract"
            element={
              <Suspense fallback={<RouteLoader />}>
                <LegacyContractDemo />
              </Suspense>
            }
          />
          <Route
            path="demo-contract/new"
            element={
              <Suspense fallback={<RouteLoader />}>
                <LegacyContractDemo />
              </Suspense>
            }
          />
          <Route
            path="demo-contract/:contractId/edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <LegacyContractDemo />
              </Suspense>
            }
          />
          <Route
            path="demo-contract/:contractId"
            element={
              <Suspense fallback={<RouteLoader />}>
                <LegacyContractDemo />
              </Suspense>
            }
          />
          <Route
            path="legacy-demo"
            element={
              <Suspense fallback={<RouteLoader />}>
                <LegacyContractDemo />
              </Suspense>
            }
          />
          <Route
            path="legacy-demo/new"
            element={
              <Suspense fallback={<RouteLoader />}>
                <LegacyContractDemo />
              </Suspense>
            }
          />
          <Route
            path="legacy-demo/:contractId/edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <LegacyContractDemo />
              </Suspense>
            }
          />
          <Route
            path="legacy-demo/:contractId"
            element={
              <Suspense fallback={<RouteLoader />}>
                <LegacyContractDemo />
              </Suspense>
            }
          /> */}
          <Route
            path=":id/edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractCreateEdit />
              </Suspense>
            }
          />
          <Route
            path="masters"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractVendorMasterList />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractDetails />
              </Suspense>
            }
          />
          <Route
            path="approvals"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractApprovals />
              </Suspense>
            }
          />
          <Route
            path="reports"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractReports />
              </Suspense>
            }
          />
          <Route
            path="pnl-dashboard"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PnlDashboard />
              </Suspense>
            }
          />
        </Route>
        <Route path="customer-statement" errorElement={<NotFound />}>
          <Route
            path="reports"
            element={
              <Suspense fallback={<RouteLoader />}>
                <CustomerReports />
              </Suspense>
            }
          />
        </Route>

        <Route path="travel" errorElement={<NotFound />}>
          <Route
            path="home"
            element={
              <Suspense fallback={<RouteLoader />}>
                <TravelHomePage />
              </Suspense>
            }
          />
          <Route
            path="all"
            element={
              <Suspense fallback={<RouteLoader />}>
                <TravelReqHomePage />
              </Suspense>
            }
          />
          <Route
            path="add"
            element={
              <Suspense fallback={<RouteLoader />}>
                <TravelReqCreatePage />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <TravelReqDetailPage />
              </Suspense>
            }
          />
          <Route
            path=":id/edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <TravelReqEditPage />
              </Suspense>
            }
          />
          <Route
            path="approvals"
            element={
              <Suspense fallback={<RouteLoader />}>
                <TravelReqApprovalPage />
              </Suspense>
            }
          />
          <Route
            path="reports"
            element={
              <Suspense fallback={<RouteLoader />}>
                <TravelReqReportPage />
              </Suspense>
            }
          />
        </Route>

        {/* Secure Masking Routes */}
        <Route
          path="secure-masking"
          errorElement={<NotFound />}
          element={<RoleProtectedRoute allowedRoles={[SystemRoles.ADMIN]} />}
        >
          <Route
            path="dashboard"
            element={
              <Suspense fallback={<RouteLoader />}>
                <SecureMaskingDashboard />
              </Suspense>
            }
          />
        </Route>

        <Route path="activity" errorElement={<NotFound />}>
          <Route
            path="home"
            element={
              <Suspense fallback={<RouteLoader />}>
                <MyActivity />
              </Suspense>
            }
          />
          <Route
            path="dashboard"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityDashboard />
              </Suspense>
            }
          />
          <Route
            path="filter"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityGlobalFilter />
              </Suspense>
            }
          />
          <Route
            path="all"
            element={<Navigate to="/activity/home" replace />}
          />
          <Route
            path=":id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <MyActivityDetail />
              </Suspense>
            }
          />
          <Route
            path="assignment"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityUserAssignment />
              </Suspense>
            }
          />
          <Route
            path="create-assignment"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityCreateUserAssignment />
              </Suspense>
            }
          />
          <Route
            path="assignment/:id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityUserAssginmentDetail />
              </Suspense>
            }
          />
          <Route
            path="approvals"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityApprovals />
              </Suspense>
            }
          />
          <Route
            path="approval/:id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityApprovalDetails />
              </Suspense>
            }
          />
          <Route
            path="reports"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ActivityReport />
              </Suspense>
            }
          />
        </Route>

        <Route path="advanced-payment" errorElement={<NotFound />}>
          <Route
            path="home"
            element={
              <Suspense fallback={<RouteLoader />}>
                <ContractHomePage />
              </Suspense>
            }
          />
          <Route
            path="all"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AdvancedPaymentHome />
              </Suspense>
            }
          />
          <Route
            path=":id/edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AdvancedPaymentCreateEdit />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AdvancedPaymentDetails />
              </Suspense>
            }
          />
          <Route
            path="approvals"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AdvancedPaymentApprovals />
              </Suspense>
            }
          />
          <Route
            path="reports"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AdvancedPaymentReports />
              </Suspense>
            }
          />
        </Route>

        <Route path="purchase-order" errorElement={<NotFound />}>
          <Route
            path="home"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderDashboard />
              </Suspense>
            }
          />
          <Route
            path="registry"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderHome mode="registry" />
              </Suspense>
            }
          />
          <Route
            path="approvals"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderHome mode="approvals" />
              </Suspense>
            }
          />
          <Route
            path="reports"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderHome mode="reports" />
              </Suspense>
            }
          />
          <Route
            path="form-field-mappings"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderConfigurationHome mode="form-fields" />
              </Suspense>
            }
          />
          <Route
            path="approval-mappings"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderConfigurationHome mode="approvals" />
              </Suspense>
            }
          />
          <Route
            path="master-defaults"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderMasterDefaults />
              </Suspense>
            }
          />
          <Route
            path="financial-policies"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderFinancialPolicies />
              </Suspense>
            }
          />
          <Route
            path="form-field-mappings/new"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderConfigurationEditor mode="form-fields" />
              </Suspense>
            }
          />
          <Route
            path="form-field-mappings/:id/edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderConfigurationEditor mode="form-fields" />
              </Suspense>
            }
          />
          <Route
            path="form-field-mappings/:id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderConfigurationEditor mode="form-fields" readOnly />
              </Suspense>
            }
          />
          <Route
            path="approval-mappings/new"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderConfigurationEditor mode="approvals" />
              </Suspense>
            }
          />
          <Route
            path="approval-mappings/:id/edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderConfigurationEditor mode="approvals" />
              </Suspense>
            }
          />
          <Route
            path="approval-mappings/:id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderConfigurationEditor mode="approvals" readOnly />
              </Suspense>
            }
          />
          <Route
            path="all"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderHome mode="registry" />
              </Suspense>
            }
          />
          <Route
            path=":id/edit"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderCreateEdit />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <PurchaseOrderDetails />
              </Suspense>
            }
          />
        </Route>

        <Route
          path="warehouse-structure"
          element={
            <Suspense fallback={<RouteLoader />}>
              <WarehouseStructure />
            </Suspense>
          }
        />
        <Route
          path="warehouse-operation"
          element={
            <Suspense fallback={<RouteLoader />}>
              <WarehouseOperationList />
            </Suspense>
          }
        />
        <Route
          path="warehouse-operation/:id/detail"
          element={
            <Suspense fallback={<RouteLoader />}>
              <WarehouseOperationDetail />
            </Suspense>
          }
        />
        <Route
          path="warehouse-operation/:id"
          element={
            <Suspense fallback={<RouteLoader />}>
              <WarehouseOperation />
            </Suspense>
          }
        />
        <Route
          path="warehouse-inventory-report"
          element={
            <Suspense fallback={<RouteLoader />}>
              <WarehouseInventoryReport />
            </Suspense>
          }
        />
        <Route path="warehouse" errorElement={<NotFound />}>
          <Route
            path="bin-details/:id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <WarehouseBinDetails />
              </Suspense>
            }
          />
        </Route>

        <Route
          path="analytics"
          errorElement={<NotFound />}
          element={
            <RoleProtectedRoute
              allowedRoles={[
                SystemRoles.ANALYTICS_ADMIN,
                SystemRoles.ANALYTICS_BUILDER,
                SystemRoles.ANALYTICS_VIEWER,
              ]}
            />
          }
        >
          <Route
            path="dashboards"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AnalyticsDashboardList />
              </Suspense>
            }
          />
          <Route
            path="dashboards/:id"
            element={
              <Suspense fallback={<RouteLoader />}>
                <AnalyticsDashboardViewer />
              </Suspense>
            }
          />
          <Route
            path="dashboards/new"
            element={
              <RoleProtectedRoute
                allowedRoles={[
                  SystemRoles.ANALYTICS_ADMIN,
                  SystemRoles.ANALYTICS_BUILDER,
                ]}
              />
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AnalyticsDashboardBuilder />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="dashboards/:id/edit"
            element={
              <RoleProtectedRoute
                allowedRoles={[
                  SystemRoles.ANALYTICS_ADMIN,
                  SystemRoles.ANALYTICS_BUILDER,
                ]}
              />
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AnalyticsDashboardBuilder />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="charts"
            element={
              <RoleProtectedRoute
                allowedRoles={[
                  SystemRoles.ANALYTICS_ADMIN,
                  SystemRoles.ANALYTICS_BUILDER,
                ]}
              />
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AnalyticsChartList />
                </Suspense>
              }
            />
            <Route
              path="new"
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AnalyticsChartBuilder />
                </Suspense>
              }
            />
            <Route
              path=":id/edit"
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AnalyticsChartBuilder />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="datasets"
            element={
              <RoleProtectedRoute
                allowedRoles={[
                  SystemRoles.ANALYTICS_ADMIN,
                  SystemRoles.ANALYTICS_BUILDER,
                ]}
              />
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AnalyticsDatasetList />
                </Suspense>
              }
            />
            <Route
              path="new"
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AnalyticsDatasetWizard />
                </Suspense>
              }
            />
            <Route
              path=":id"
              element={
                <Suspense fallback={<RouteLoader />}>
                  <AnalyticsDatasetDetail />
                </Suspense>
              }
            />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} errorElement={<NotFound />} />
      </Route>

      <Route path="*" element={<NotFound />} errorElement={<NotFound />} />
    </Route>,
  ),
);

const Index = () => {
  return <RouterProvider router={router} />;
};

export default Index;
